/**
 * Which plan features a tenant can use, as one answer per feature that both enforcement and the
 * menu read. Client-safe: the loaders live in `load.ts`.
 */

import { SUBSCRIPTION_STATUSES, VOICE_TRIAL_STATUSES } from '../constants'

/** `voice`: creating voice agents; `voice_agents`: the Voice Agents page. */
export const PLAN_FEATURES = ['campaigns', 'voice', 'voice_agents'] as const

export type PlanFeature = (typeof PLAN_FEATURES)[number]

export type FeatureLockReason = 'no_plan' | 'not_in_plan' | 'not_during_trial' | 'trial_used'

export type FeatureAccess = { available: true } | { available: false; reason: FeatureLockReason }

export type PlanFeatureAccess = Record<PlanFeature, FeatureAccess>

export interface CampaignsPlanRow {
  campaignsEnabled: boolean
  campaignsTrialEnabled: boolean
  subscriptionStatus: string | null
}

const AVAILABLE: FeatureAccess = { available: true }

/** `null` = the tenant has no subscription. */
export function resolveCampaignsAccess(row: CampaignsPlanRow | null): FeatureAccess {
  if (!row) return { available: false, reason: 'no_plan' }
  if (!row.campaignsEnabled) return { available: false, reason: 'not_in_plan' }
  if (row.subscriptionStatus === SUBSCRIPTION_STATUSES.TRIALING && !row.campaignsTrialEnabled) {
    return { available: false, reason: 'not_during_trial' }
  }
  return AVAILABLE
}

/** `used` = the tenant's one voice trial has ended (see {@link usesUpVoiceTrial}). */
export type VoiceTrialState = 'none' | 'active' | 'used'

export interface VoiceTrialRecord {
  status: string
  spentMicrousd: number
}

/**
 * A trial cancelled before any billed use gives the trial back — an onboarding retry deletes the
 * agent it just created. Every other ended trial uses it up.
 */
export function usesUpVoiceTrial({ status, spentMicrousd }: VoiceTrialRecord): boolean {
  return !(status === VOICE_TRIAL_STATUSES.CANCELLED && spentMicrousd === 0)
}

/** Shared by the Supabase and Drizzle readers of the trial rows. */
export function voiceTrialState(trials: readonly VoiceTrialRecord[]): VoiceTrialState {
  if (trials.some((trial) => trial.status === VOICE_TRIAL_STATUSES.ACTIVE)) return 'active'
  return trials.some(usesUpVoiceTrial) ? 'used' : 'none'
}

export interface VoicePlanFacts {
  voiceIncluded: boolean
  voiceTrialEnabled: boolean
  trial: VoiceTrialState
}

/** A tenant gets one voice trial, on a plan that offers one and does not include voice. */
export function canStartVoiceTrial({ voiceIncluded, voiceTrialEnabled, trial }: VoicePlanFacts): boolean {
  return voiceTrialEnabled && !voiceIncluded && trial === 'none'
}

/** Voice is usable — agents can be created — while it is included, a trial runs, or a trial can start. `null` = no subscription. */
export function resolveVoiceAccess(facts: VoicePlanFacts | null): FeatureAccess {
  if (!facts) return { available: false, reason: 'no_plan' }
  if (facts.voiceIncluded || facts.trial === 'active' || canStartVoiceTrial(facts)) return AVAILABLE
  return { available: false, reason: facts.voiceTrialEnabled && facts.trial === 'used' ? 'trial_used' : 'not_in_plan' }
}

export interface VoiceAgentsFacts extends VoicePlanFacts {
  hasVoiceAgent: boolean
}

/** The page also stays open for an existing agent: inbound calls still reach it, so the tenant must be able to manage it. */
export function resolveVoiceAgentsAccess(facts: VoiceAgentsFacts | null): FeatureAccess {
  const voice = resolveVoiceAccess(facts)
  return voice.available || facts?.hasVoiceAgent ? AVAILABLE : voice
}

const FEATURE_LABELS: Record<PlanFeature, string> = {
  campaigns: 'Campaigns',
  voice: 'Voice agents',
  voice_agents: 'Voice agents',
}

/** The lock's reason in customer words; null while the answer is loading or the feature is open. */
export function featureLockMessage(feature: PlanFeature, access: FeatureAccess | undefined): string | null {
  return access && !access.available ? featureLockHint(feature, access.reason) : null
}

/** Why a feature is locked, in customer words. */
export function featureLockHint(feature: PlanFeature, reason: FeatureLockReason): string {
  switch (reason) {
    case 'no_plan':
      return 'Your workspace has no active plan.'
    case 'not_in_plan':
      return `${FEATURE_LABELS[feature]} are not included in your plan.`
    case 'not_during_trial':
      return `${FEATURE_LABELS[feature]} are not available during your plan's trial.`
    case 'trial_used':
      return `Your trial of ${FEATURE_LABELS[feature].toLowerCase()} has ended, and your plan does not include them.`
  }
}
